import type { ReactNode } from 'react';
import { Icon } from './icons';

export type StepState = 'done' | 'current' | 'todo';

/** Horizontal step cards with check / number (design/Import "Datei hochgeladen · Spalten zuordnen · Prüfung"). */
export function StepCards({ label, steps }: { label: string; steps: { title: ReactNode; sub?: ReactNode; state: StepState; onClick?: () => void }[] }) {
  return (
    <ol className="step-cards" aria-label={label}>
      {steps.map((s, i) => {
        const body = (
          <>
            <span className={`step-mark ${s.state}`} aria-hidden>{s.state === 'done' ? <Icon name="check" size={16} strokeWidth={2.4} /> : i + 1}</span>
            <span className="step-text">
              <strong>{s.title}</strong>
              {s.sub && <span>{s.sub}</span>}
            </span>
          </>
        );
        return (
          <li key={i} className={`step-card ${s.state}`} aria-current={s.state === 'current' ? 'step' : undefined}>
            {s.onClick ? <button type="button" className="step-card-btn" onClick={s.onClick}>{body}</button> : <div className="step-card-btn">{body}</div>}
          </li>
        );
      })}
    </ol>
  );
}

/** Vertical numbered steps joined by a line (design/Sicherheit "2-Faktor einrichten"). */
export function StepList({ label, steps }: { label: string; steps: { title: ReactNode; state: StepState; children?: ReactNode }[] }) {
  return (
    <ol className="vsteps" aria-label={label}>
      {steps.map((s, i) => (
        <li key={i} className={`vstep ${s.state}`} aria-current={s.state === 'current' ? 'step' : undefined}>
          <span className="vstep-mark" aria-hidden>{s.state === 'done' ? <Icon name="check" size={16} strokeWidth={2.4} /> : i + 1}</span>
          <div className="vstep-body">
            <strong className="vstep-title">{s.title}</strong>
            {s.children}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Dated history with coloured dots (design/Mahnung "Verlauf"). Tone carries meaning together with the text. */
export function EventTrail({ label, items }: { label: string; items: { date: ReactNode; title: ReactNode; sub?: ReactNode; tone?: 'ok' | 'err' | 'warn' | 'brand' | 'neutral' }[] }) {
  return (
    <ol className="event-trail" aria-label={label}>
      {items.map((x, i) => (
        <li key={i}>
          <span className={`trail-dot ${x.tone ?? 'brand'}`} aria-hidden />
          <span className="trail-date">{x.date}</span>
          <span className="trail-text">
            <strong>{x.title}</strong>
            {x.sub && <span>{x.sub}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** What someone may / may not do, with check or minus marks (design/MieterEinladen "Was die Mieter sehen dürfen"). */
export function PermissionList({ items, label }: { items: { text: ReactNode; allowed: boolean }[]; label?: string }) {
  return (
    <ul className="perm-list" aria-label={label}>
      {items.map((x, i) => (
        <li key={i} className={x.allowed ? 'yes' : 'no'}>
          <span className="perm-mark" aria-hidden>{x.allowed && <Icon name="check" size={14} strokeWidth={2.4} />}</span>
          <span>{x.text}</span>
        </li>
      ))}
    </ul>
  );
}
